// My Team and its tools, as one place (PLAN-2026-10-17 §3, B-400).
//
// A LEAGUE OPENS ON MY TEAM (B-211): the roster, where a lineup is set during
// the week and its points are followed on game days. A league still in setup
// has no roster yet, so it keeps the pre-draft Home (the countdown and the
// invite link); a real one in setup never gets here, because the gate in
// `App.tsx` draws its own pre-draft page.
//
// `/l/:id` is My Team with a tool beside the lineup. From a tablet up, each
// tool's own address — `/players`, `/waivers`, `/trades`, `/plan`, `/news` —
// is My Team with that tool taking the room, the roster narrowed to a dock:
// moving between My Team and Players is the page changing shape, not a new
// page, and the roster stays on screen while the pool is browsed, which is
// the comparison being made. The same component draws all of them, so the
// page is not drawn from blank as it changes shape (`shell/morph.ts`).
//
// ON A PHONE each address is the tool's own screen, as it always was:
// Players is a tab of its own, and the rest open over My Team with a Back to
// it (B-398). Where a tool's address asks for something that is not mine —
// the league's activity, another team's weeks ahead — it is that screen too,
// at every width. So is a league before its draft, which has no roster yet.

import { useSearchParams } from 'react-router-dom';
import { useActiveLeague } from '../data/LeaguesProvider';
import { useSim } from '../data/SimProvider';
import { useLeagueSource } from '../data/leagueSource';
import { useMyTeamId } from '../data/hooks';
import { useWindowTier } from '../shell/useTier';
import { HomeScreen } from './HomeScreen';
import { LineupScreen } from './LineupScreen';
import type { SideTool } from './myTeam/TeamSide';
import { ToolScreen } from './myTeam/ToolRoom';
import { LeagueSection } from './league/LeagueSection';

export function TeamPlace({ tool }: { tool?: SideTool }) {
  const real = useActiveLeague();
  const { state } = useSim();
  const source = useLeagueSource();
  const myTeamId = useMyTeamId();
  const tier = useWindowTier();
  const [params] = useSearchParams();
  const setup = real !== null ? real.league.status === 'setup' : state.status === 'setup';
  if (tool === undefined) return setup ? <HomeScreen /> : <LineupScreen />;
  // THE LEAGUE'S NEWS, not my players' (the front page's wire, B-403), is a
  // League section: the News tool with another filter than mine.
  const filter = params.get('filter');
  if (tool === 'news' && filter !== null && filter !== 'mine')
    return (
      <LeagueSection>
        <ToolScreen tool="news" />
      </LeagueSection>
    );
  const notMine =
    (tool === 'waivers' && params.get('show') === 'log') ||
    (tool === 'plan' && params.get('team') !== null && params.get('team') !== myTeamId) ||
    // The practice league plays one week, so it has no weeks ahead to plan.
    (tool === 'plan' && source === 'sim') ||
    // A seat that has no team (a commissioner who does not play) has no roster.
    (source !== 'undecided' && myTeamId === null);
  if (setup || tier === 'phone' || notMine) return <ToolScreen tool={tool} />;
  return <LineupScreen room={tool} />;
}
